import React, { useEffect, useMemo, useState } from 'react';

const API_BASE = window.location.origin;

const formatDuration = seconds => {
  if (seconds === null || seconds === undefined) return 'Not recorded';
  const value = Math.max(0, Math.round(seconds));
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
};

const formatDate = value => value ? new Date(value).toLocaleString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '-';

export default function QuizAuditPage() {
  const [teams, setTeams] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [audit, setAudit] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('adminToken');

  const headers = { Authorization: `Bearer ${token}` };
  const logout = () => {
    localStorage.removeItem('adminToken');
    window.location.assign('/admin/control');
  };

  useEffect(() => {
    if (!token) return logout();
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/quiz-audits`, { headers, cache: 'no-store' });
        if (response.status === 401) return logout();
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Unable to load quiz audits.');
        setTeams(data.teams || []);
      } catch (requestError) {
        setError(requestError.message || 'Unable to load quiz audits.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredTeams = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return teams;
    return teams.filter(team => `${team.teamCode} ${team.participants}`.toLowerCase().includes(query));
  }, [filter, teams]);

  async function openAudit(teamId) {
    setSelectedId(teamId);
    setAudit(null);
    setError('');
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/quiz-audits/${teamId}`, { headers, cache: 'no-store' });
      if (response.status === 401) return logout();
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Unable to load this quiz audit.');
      setAudit(data);
    } catch (requestError) {
      setError(requestError.message || 'Unable to load this quiz audit.');
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <main className="admin-container audit-page">
      <header className="admin-header">
        <div>
          <h1>Online Quiz Answer Audit</h1>
          <p className="admin-subtitle">Private record of final answers and server-timed question durations.</p>
        </div>
        <div className="admin-actions">
          <a className="refresh-btn audit-link" href="/admin/control">Back to control</a>
          <button className="logout-btn" type="button" onClick={logout}>Logout</button>
        </div>
      </header>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="audit-layout">
        <aside className="audit-team-list" aria-label="Submitted teams">
          <label className="audit-filter-label" htmlFor="audit-team-filter">Find a team</label>
          <input id="audit-team-filter" value={filter} onChange={event => setFilter(event.target.value)} placeholder="Team code or participant" />
          <p className="audit-list-count">{filteredTeams.length} submitted team{filteredTeams.length === 1 ? '' : 's'}</p>
          {loading ? <p className="loading">Loading audits...</p> : filteredTeams.length === 0 ? <p className="no-data">No submitted teams found.</p> : (
            <div className="audit-team-buttons">
              {filteredTeams.map(team => (
                <button key={team.id} type="button" className={`audit-team-button ${selectedId === team.id ? 'selected' : ''}`} onClick={() => openAudit(team.id)} aria-pressed={selectedId === team.id}>
                  <strong>{team.teamCode}</strong>
                  <span>{team.participants || 'Participant details unavailable'}</span>
                  <small>{team.score}/{team.total} · {formatDuration(team.timeTaken)}</small>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="audit-detail" aria-live="polite" aria-busy={detailLoading}>
          {!selectedId && <div className="audit-empty"><h2>Select a team</h2><p>Choose a submitted team to view its private answer audit.</p></div>}
          {detailLoading && <div className="loading">Loading team audit...</div>}
          {audit && !detailLoading && <>
            <div className="audit-detail-header">
              <div><h2>{audit.team.teamCode}</h2><p>{audit.team.participants || 'Participant details unavailable'}</p></div>
              <dl className="audit-metrics">
                <div><dt>Score</dt><dd>{audit.team.score}/{audit.team.total}</dd></div>
                <div><dt>Total time</dt><dd>{formatDuration(audit.team.timeTaken)}</dd></div>
                <div><dt>Submitted</dt><dd>{formatDate(audit.team.submittedAt)}</dd></div>
              </dl>
            </div>
            <p className="audit-note">“Not recorded” applies to attempts submitted before per-question timing was enabled.</p>
            <div className="table-scroll">
              <table className="audit-table">
                <thead><tr><th>Question</th><th>Selected answer</th><th>Correct answer</th><th>Outcome</th><th>Time</th></tr></thead>
                <tbody>{audit.questions.map(question => (
                  <tr key={question.questionIndex}>
                    <td><strong>Q{question.questionIndex + 1}</strong><span>{question.text}</span></td>
                    <td>{question.selected ? <><strong>{question.selected.label}</strong> — {question.selected.text}</> : 'Unanswered'}</td>
                    <td><strong>{question.correct.label}</strong> — {question.correct.text}</td>
                    <td><span className={`audit-outcome ${question.outcome.toLowerCase()}`}>{question.outcome}</span></td>
                    <td className="audit-time">{question.viewed === false ? 'Not viewed' : formatDuration(question.timeSpentSeconds)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </>}
        </section>
      </div>
    </main>
  );
}
